/* eslint-disable no-nested-ternary */
import { Box, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import uniqid from 'uniqid';
import { Reservation } from '../../apiTypes';
import EditModal from '../../components/EditModal';
import FormSelect from '../../components/forms/FormSelect';
import FormTextField from '../../components/forms/FormTextField';
import { timeSlots } from '../../constants/dates';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useLocale } from '../../contexts/locale.context';
import { setDoc, updateDoc } from '../../utils/api';

type Props = {
  reservaion?: DbDocument<Reservation> | null;
  onClose: () => void;
};

const EditReservaion = ({ reservaion, onClose }: Props) => {
  const { cafeSlug, tablesMap, tablesByIds } = useAdmin();
  const { lang } = useLocale();

  const tables = useMemo(() => tablesMap.tables.map((t) => t.id), [tablesMap.tables]);

  const form = useForm<Reservation>({
    mode: 'onChange',
    defaultValues: {
      date: reservaion?.date || Date.now(),
      name: reservaion?.name || '',
      phone: reservaion?.phone || '',
      guests: reservaion?.guests || '1-2',
      tableId: reservaion?.tableId || '',
      time: reservaion?.time || '',
      confirmed: true,
      canceled: reservaion?.canceled || false,
    },
  });

  const date = form.watch('date');
  const guests = form.watch('guests');

  const handleClose = () => {
    onClose();
    form.reset();
  };

  const saveChanges = (values: Reservation) => {
    if (reservaion?.id) {
      updateDoc<Reservation>(`cafes/${cafeSlug}/reservations/${reservaion.id}`, values);
    } else {
      setDoc<Reservation>(`cafes/${cafeSlug}/reservations/${uniqid()}`, values);
    }

    handleClose();
  };

  const cancel = async () => {
    if (reservaion?.id) {
      updateDoc<Reservation>(`cafes/${cafeSlug}/reservations/${reservaion.id}`, { canceled: true, confirmed: true });
      handleClose();
    }
  };

  return (
    <EditModal<Reservation>
      title={t.table_reservation[lang]}
      open
      onClose={handleClose}
      submitButton={reservaion ? (reservaion.canceled ? '' : t.confirm[lang]) : t.add[lang]}
      onSubmit={reservaion?.canceled ? undefined : saveChanges}
      secondaryButton={reservaion && !reservaion.canceled ? t.cancel_booking[lang] : undefined}
      confirmText={t.cancel_reservation_confirm[lang]}
      onSecondaryButtonClick={cancel}
      form={form}
      size="xs"
    >
      <Box sx={{ py: 2, mb: 1 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <DatePicker
            value={date}
            onChange={(d) => {
              if (d) {
                form.setValue('date', d);
                form.setValue('time', '');
              }
            }}
            label={t.date[lang]}
            disablePast
            renderInput={(props) => <TextField fullWidth {...props} />}
          />

          <FormSelect label={t.time[lang]} name="time" options={timeSlots} />
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="body2" sx={{ ml: 2 }} color={(t) => t.palette.text.secondary}>
            {t.quantity_of_visitors[lang]}
          </Typography>

          <ToggleButtonGroup value={guests} exclusive onChange={(_, v) => form.setValue('guests', v)} fullWidth>
            {['1-2', '3-4', '5-6', '6+'].map((value) => (
              <ToggleButton key={value} value={value}>
                {value}
              </ToggleButton>
            ))}
          </ToggleButtonGroup>
        </Box>

        <FormTextField sx={{ mb: 3 }} name="name" label={t.userName[lang]} fullWidth />

        <FormTextField sx={{ mb: 3 }} name="phone" label={t.phone[lang]} fullWidth />

        <FormSelect
          name="tableId"
          label={t.table[lang]}
          options={tables}
          renderOption={(id) => <Typography>{tablesByIds[id]?.name}</Typography>}
        />
      </Box>
    </EditModal>
  );
};

export default EditReservaion;
